import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, UserPlus, Sparkles, CheckCircle2, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/pricing?trial=true';
  const refCode = searchParams.get('ref') || '';
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    role: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms & Confidentiality Policy');
      return;
    }

    setLoading(true);
    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.company || null,
        formData.role || null,
        refCode || null
      );
      toast.success('Welcome to PropertyARV!');
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'AI-powered property analysis',
    'Comp finder with reactive filters',
    'Full project management suite',
    'CRM, eSign, and business directory'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 pt-20 transition-colors">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Benefits */}
          <div className="space-y-8">
            <div>
              <Badge className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-pink-400 border-0 mb-4">
                <Sparkles className="w-3 h-3 mr-1" />
                Start Your 7-Day Free Trial
              </Badge>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Create Your Account
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Join thousands of investors using AI-powered property analysis 
                to find better deals and maximize returns.
              </p>
            </div>

            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="bg-purple-100 dark:bg-pink-900/30 rounded-xl p-6">
              <p className="text-purple-800 dark:text-pink-400 font-medium mb-2">
                7-Day Free Trial on All Plans
              </p>
              <p className="text-purple-600 dark:text-pink-400 text-sm">
                Create your account, pick a plan, and enjoy full access for 7 days
                free. Your card won't be charged until the trial ends.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          {refCode && (
            <div className="lg:col-span-2 mb-0">
              <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl px-4 py-3 max-w-md mx-auto" data-testid="referral-banner">
                <Gift className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <p className="text-sm text-purple-700 dark:text-purple-300">You were referred! Sign up to get started.</p>
              </div>
            </div>
          )}
          <Card className="shadow-xl border-purple-100 dark:border-purple-900/50 dark:bg-gray-800">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">{t('auth.createAccount')}</CardTitle>
              <CardDescription>
                Already have an account?{' '}
                <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                  Sign in
                </Link>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('auth.fullName')}</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder={t('auth.johnSmith')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="register-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="register-email"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="register-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('auth.confirm')}</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">{t('auth.companyOptional')}</Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder={t('auth.yourCompanyName')}
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="register-company"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">{t('auth.yourRoleOptional')}</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="register-role">
                      <SelectValue placeholder={t('auth.selectYourRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="investor">{t('auth.realEstateInvestor')}</SelectItem>
                      <SelectItem value="agent">{t('auth.realEstateAgent')}</SelectItem>
                      <SelectItem value="wholesaler">{t('auth.wholesaler')}</SelectItem>
                      <SelectItem value="flipper">{t('auth.houseFlipper')}</SelectItem>
                      <SelectItem value="property_manager">{t('auth.propertyManager')}</SelectItem>
                      <SelectItem value="other">{t('auth.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="agree-terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    data-testid="register-agree-terms"
                  />
                  <label htmlFor="agree-terms" className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="text-purple-600 dark:text-purple-400 hover:underline">Terms of Service</Link>
                    ,{' '}
                    <Link to="/privacy" target="_blank" className="text-purple-600 dark:text-purple-400 hover:underline">Privacy Policy</Link>
                    {' '}&{' '}
                    <Link to="/confidentiality" target="_blank" className="text-purple-600 dark:text-purple-400 hover:underline">Confidentiality Policy</Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white py-6"
                  disabled={loading || !agreedToTerms}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By registering, you agree to our{' '}
                  <Link to="/terms" className="text-purple-600 dark:text-purple-400 hover:underline">Terms of Service</Link>
                  ,{' '}
                  <Link to="/privacy" className="text-purple-600 dark:text-purple-400 hover:underline">Privacy Policy</Link>
                  {' '}&{' '}
                  <Link to="/confidentiality" className="text-purple-600 dark:text-purple-400 hover:underline">Confidentiality Policy</Link>.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
